import { useState } from 'react'
import { Input } from '@heroui/react'
import { ApiError, api } from '../../lib/api'
import { readProfileFile } from '../../lib/profilesBranches'
import { problemText } from '../../lib/profilesProblems'
import { explain } from '../../lib/staffErrors'
import type { StaffKey } from '../../lib/staffText'
import type { ImportItem, ImportRequest, OperationProfileRow, ProfileFile, ProfileImportPreview, ProfileProblem } from '../../lib/types'
import { FilePicker } from '../../ui/FilePicker'
import { useStaffText } from '../../ui/StaffText'
import { InnerPage, type Crumb } from '../settingsKit'
import { Choices } from '../setupKit'

const FILE_ERROR: Record<'notJson' | 'notProfile' | 'version', StaffKey> = {
  notJson: 'profiles.import.notJson',
  notProfile: 'profiles.import.notProfile',
  version: 'profiles.import.version',
}

const KIND: Record<ImportItem['kind'], StaffKey> = {
  group: 'profiles.group',
  segment: 'profiles.kind.segment',
  queue: 'profiles.queue',
  workProfile: 'profiles.workProfile',
  equipmentProfile: 'profiles.kind.equipmentProfile',
  unit: 'profiles.kind.unit',
}

// Names past this many fold under a "N more" toggle.
const SHOWN = 8

/** Import an operation profile file: as a new profile, or onto the one of the same name, after a preview. */
export function ImportSheet({
  organisation,
  profiles,
  onClose,
  trail,
  onImported,
}: {
  organisation?: string
  profiles: readonly OperationProfileRow[]
  onClose: () => void
  /** The path back: the profiles list, and the profile when one is open. */
  trail: Crumb[]
  onImported: (id: string) => void
}) {
  const { say, lang } = useStaffText()
  const [file, setFile] = useState<ProfileFile | null>(null)
  const [fileError, setFileError] = useState('')
  const [name, setName] = useState('')
  const [onto, setOnto] = useState<OperationProfileRow | null>(null)
  const [mode, setMode] = useState<'new' | 'onto'>('new')
  const [preview, setPreview] = useState<{ key: string; answer: ProfileImportPreview } | null>(null)
  const [problems, setProblems] = useState<ProfileProblem[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function picked(f: File) {
    setPreview(null)
    setProblems([])
    setError('')
    const read = readProfileFile(await f.text())
    if ('error' in read) {
      setFile(null)
      setFileError(say(FILE_ERROR[read.error]))
      return
    }
    setFileError('')
    setFile(read.file)
    setName(read.name)
    const key = read.name.toLocaleLowerCase(lang)
    const same = read.name ? (profiles.find((p) => p.name.trim().toLocaleLowerCase(lang) === key) ?? null) : null
    setOnto(same)
    const nextMode = same ? 'onto' : 'new'
    setMode(nextMode)
    if (same || read.name) void check(read.file, nextMode, read.name, same)
  }

  const body = (f: ProfileFile, m: 'new' | 'onto', n: string, target: OperationProfileRow | null): ImportRequest =>
    m === 'onto' && target
      ? { file: f, onto: target.id, baseRevision: target.revision }
      : { file: f, name: n.trim(), ...(organisation ? { organisation } : {}) }

  // What the preview answered for: a different choice or name needs a new one before Import.
  const keyOf = (m: 'new' | 'onto', n: string) => (m === 'onto' ? `onto:${onto?.id}` : `new:${n.trim()}`)
  const current = preview && preview.key === keyOf(mode, name) ? preview.answer : null

  async function check(f: ProfileFile, m: 'new' | 'onto', n: string, target: OperationProfileRow | null) {
    setBusy(true)
    setError('')
    setProblems([])
    try {
      const answer = await api.previewImportOperationProfile(body(f, m, n, target))
      setPreview({ key: m === 'onto' ? `onto:${target?.id}` : `new:${n.trim()}`, answer })
    } catch (err) {
      setPreview(null)
      fail(err)
    } finally {
      setBusy(false)
    }
  }

  async function commit(f: ProfileFile) {
    setBusy(true)
    setError('')
    try {
      const { profile } = await api.importOperationProfile(body(f, mode, name, onto))
      onImported(profile.id)
    } catch (err) {
      fail(err)
    } finally {
      setBusy(false)
    }
  }

  function fail(err: unknown) {
    if (err instanceof ApiError && err.problems?.length) setProblems(err.problems)
    else setError(explain(err, lang, 'common.unreachable'))
  }

  function choose(m: 'new' | 'onto') {
    setMode(m)
    if (file && (m === 'onto' || name.trim() !== '')) void check(file, m, name, onto)
  }

  const blocking = (current?.problems ?? []).some((p) => !p.warning)
  const ready = !!file && (mode === 'onto' ? !!onto : name.trim() !== '')

  return (
    <InnerPage
      before={trail}
      title={say('profiles.import.title')}
      wide
      onClose={onClose}
      onSubmit={() => {
        if (!file) return
        if (current) void commit(file)
        else void check(file, mode, name, onto)
      }}
      submitLabel={current ? say('profiles.import.go') : say('profiles.import.check')}
      valid={ready && !blocking}
      busy={busy}
      error={error}
    >
      <div className="field">
        <FilePicker accept=".json,application/json" onFile={(f) => void picked(f)} />
        {fileError && (
          <p className="pf-import__bad" role="alert">
            {fileError}
          </p>
        )}
      </div>

      {file && (
        <Choices
          label={say('profiles.import.title')}
          value={mode}
          onChange={choose}
          options={[
            { value: 'new' as const, label: say('profiles.import.asNew') },
            ...(onto ? [{ value: 'onto' as const, label: say('profiles.import.onto', { name: onto.name }) }] : []),
          ]}
        />
      )}
      {file && mode === 'new' && (
        <label className="field">
          <span>{say('common.name')}</span>
          <Input fullWidth value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </label>
      )}

      {current && (
        <div className="pf-import__preview">
          <ItemList title={say('profiles.import.added')} items={current.added} />
          {current.mode === 'onto' && (
            <>
              <ItemList title={say('profiles.import.changed')} items={current.changed} />
              <ItemList title={say('profiles.import.kept')} items={current.kept} />
              <ItemList title={say('profiles.import.notInFile')} items={current.notInFile} />
            </>
          )}
          {current.unknownServices.length > 0 && (
            <NameList title={say('profiles.import.unknownServices')} names={current.unknownServices} />
          )}
        </div>
      )}

      {(problems.length > 0 || (current && current.problems.length > 0)) && (
        <NameList
          title={say('profiles.import.problems')}
          names={(problems.length > 0 ? problems : current!.problems).map((p) => problemText(p, lang))}
          bad
        />
      )}
    </InnerPage>
  )
}

function ItemList({ title, items }: { title: string; items: readonly ImportItem[] }) {
  const { say } = useStaffText()
  return (
    <NameList
      title={title}
      names={items.map((it) => (it.parent ? `${it.parent} › ${it.name}` : it.name))}
      kinds={items.map((it) => say(KIND[it.kind] ?? 'profiles.group'))}
    />
  )
}

function NameList({ title, names, kinds, bad }: { title: string; names: readonly string[]; kinds?: readonly string[]; bad?: boolean }) {
  const { say } = useStaffText()
  const row = (n: string, i: number) => (
    <li key={i}>
      {kinds?.[i] && <span className="pf-import__kind">{kinds[i]}</span>}
      <span>{n}</span>
    </li>
  )
  return (
    <section className="pf-import__list" data-bad={bad || undefined}>
      <h3>
        {title} <span className="pf-import__count">{names.length}</span>
      </h3>
      {names.length > 0 && <ul>{names.slice(0, SHOWN).map(row)}</ul>}
      {names.length > SHOWN && (
        <details>
          <summary>{say('profiles.import.more', { n: names.length - SHOWN })}</summary>
          <ul>{names.slice(SHOWN).map((n, i) => row(n, i + SHOWN))}</ul>
        </details>
      )}
    </section>
  )
}
